import { supabase } from "@/integrations/supabase/client";
import { logger } from "../logger";
import {
  CorporateFinancialProfile,
  CorporatePaymentCost,
  CreateFinancialProfileRequest,
  CreatePaymentCostRequest,
} from "@/types/CardProgram";

// ==================== Financial Profiles ====================

/** Fetch financial profile. */
export async function getFinancialProfile(corporateId: number, financialYear?: number): Promise<CorporateFinancialProfile | null> {
  try {
    let query = supabase
      .from('corporate_financial_profiles')
      .select('*')
      .eq('corporate_id', corporateId)
      .eq('is_active', true);

    if (financialYear) query = query.eq('financial_year', financialYear);

    const { data, error } = await query
      .order('financial_year', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error: unknown) {
    logger.error('Error in getFinancialProfile', error);
    throw error;
  }
}

/** Fetch financial profile history. */
export async function getFinancialProfileHistory(corporateId: number): Promise<CorporateFinancialProfile[]> {
  try {
    const { data, error } = await supabase
      .from('corporate_financial_profiles')
      .select('*')
      .eq('corporate_id', corporateId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error: unknown) {
    logger.error('Error in getFinancialProfileHistory', error);
    throw error;
  }
}

/** Create financial profile. */
export async function createFinancialProfile(profile: CreateFinancialProfileRequest): Promise<CorporateFinancialProfile> {
  // Deactivate current active profile, then insert new active row
  const existing = await getFinancialProfile(profile.corporate_id);
  if (existing) {
    const { error: deactivateError } = await supabase
      .from('corporate_financial_profiles')
      .update({ is_active: false })
      .eq('id', existing.id);
    if (deactivateError) throw deactivateError;
  }

  const { data, error } = await supabase
    .from('corporate_financial_profiles')
    .insert([{ ...profile, is_active: true }])
    .select()
    .single();

  if (error) throw error;
  return data;
}

/** Update financial profile. */
export async function updateFinancialProfile(id: string, updates: Partial<CreateFinancialProfileRequest>): Promise<CorporateFinancialProfile> {
  const { data, error } = await supabase
    .from('corporate_financial_profiles')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ==================== Payment Costs ====================

/** Fetch payment costs. */
export async function getPaymentCosts(corporateId: number): Promise<CorporatePaymentCost[]> {
  const { data, error } = await supabase
    .from('corporate_payment_costs')
    .select('*')
    .eq('corporate_id', corporateId)
    .eq('is_active', true)
    .order('payment_method', { ascending: true });

  if (error) throw error;
  return data || [];
}

/** Create payment cost. */
export async function createPaymentCost(cost: CreatePaymentCostRequest): Promise<CorporatePaymentCost> {
  const { data, error } = await supabase
    .from('corporate_payment_costs')
    .insert([cost])
    .select()
    .single();

  if (error) throw error;
  return data;
}

/** Update payment cost. */
export async function updatePaymentCost(id: string, cost: Partial<CreatePaymentCostRequest>): Promise<CorporatePaymentCost> {
  const { data, error } = await supabase
    .from('corporate_payment_costs')
    .update(cost)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/** Upsert payment cost. */
export async function upsertPaymentCost(
  corporateId: number,
  paymentMethod: string,
  cost: Partial<CorporatePaymentCost>
): Promise<CorporatePaymentCost> {
  const { data, error } = await supabase
    .from('corporate_payment_costs')
    // One cost row per (corporate, payment_method). Without onConflict this inserted every time,
    // so saving the same payment method twice produced duplicate rows — and the readers below use
    // .single(), which errors on the second. Constraint added in 20260803040000.
    .upsert({ corporate_id: corporateId, payment_method: paymentMethod, ...cost },
            { onConflict: 'corporate_id,payment_method' })
    .select()
    .single();

  if (error) throw error;
  return data;
}
